/**
 * sharing.ts — Export, import, and deep-link sharing for simulation state.
 *
 * Three mechanisms:
 *
 * 1. Export: download full SimulationParams as JSON file
 * 2. Import: read a JSON file and restore params
 * 3. Share link: compact base64url in URL fragment (#v1/<b64>)
 *    - Contains only the fields a recipient needs to reconstruct the scenario
 *    - No backend, no TTL, no auth — the URL IS the data
 *    - Swap the base URL for production (finance.core-loop.dev)
 *
 * Share payload intentionally excludes:
 *   - SS config (complex, recipient has different history)
 *   - businessInvestment details (personal)
 *   - projectionYears (re-derived from birthYear)
 *
 * HC-NO-PERSONAL-DATA: sharing is an explicit user action.
 */

import { deflateSync, inflateSync, strToU8, strFromU8 } from 'fflate'
import type { SimulationParams, UserProfile, SimulationEvent, AssetAllocation } from '../engine/types'
import { defaultParams } from '../engine/montecarlo'

// ─── Export / Import ──────────────────────────────────────────────────────────

export function exportToJSON(params: SimulationParams): void {
  const payload = JSON.stringify(params, null, 2)
  const blob = new Blob([payload], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `312-finance-${new Date().toISOString().slice(0, 10)}.json`
  a.click()
  URL.revokeObjectURL(url)
}

export function importFromJSON(file: File): Promise<SimulationParams> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const raw = JSON.parse(e.target?.result as string) as Partial<SimulationParams>
        // Must have a profile with at least a valid birthYear
        if (typeof raw?.profile?.birthYear !== 'number' || raw.profile.birthYear < 1900) {
          reject(new Error('Invalid export file — missing or invalid profile data'))
          return
        }
        // Merge with defaults so missing/new fields always have safe values
        const base = defaultParams()
        const merged: SimulationParams = { ...base, ...raw, profile: { ...base.profile, ...raw.profile } }
        resolve(merged)
      } catch {
        reject(new Error('Could not parse file — is this a 312-finance export?'))
      }
    }
    reader.onerror = () => reject(new Error('File read error'))
    reader.readAsText(file)
  })
}

// ─── Share link (base64url in URL fragment) ───────────────────────────────────

interface SharePayload {
  v: 1
  p: UserProfile            // profile
  ev: SimulationEvent[]     // events
  al: AssetAllocation       // allocation
  eq: number                // equityMean
  inf: number               // inflation.mean
  ret: number               // retirementTargetNetWorth
  c: number                 // annualRetirementContribution
  ry: number                // retirementYear
  pre: number               // postRetirementExpenses
}

function toBase64url(data: Uint8Array): string {
  let binary = ''
  data.forEach(b => binary += String.fromCharCode(b))
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function fromBase64urlBytes(b64: string): Uint8Array {
  const padded = b64.replace(/-/g, '+').replace(/_/g, '/') + '=='.slice(0, (4 - b64.length % 4) % 4)
  const binary = atob(padded)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes
}

function deflate(json: string): Uint8Array {
  return deflateSync(strToU8(json), { level: 9 })
}

function inflate(b64: string): string {
  return strFromU8(inflateSync(fromBase64urlBytes(b64)))
}

/** Generate a shareable URL with params encoded in the fragment.
 *  Format: https://finance.core-loop.dev/#import/v2/<deflate+base64url>
 *  The /import/ prefix namespaces share links for future extensibility.
 *  v2 = deflate-compressed JSON (v1 was plain JSON, kept for backwards decode).
 */
export function generateShareURL(params: SimulationParams): string {
  const payload: SharePayload = {
    v: 1,
    p: params.profile,
    ev: params.events ?? [],
    al: params.allocation,
    eq: params.returns.equityMean,
    inf: params.inflation.mean,
    ret: params.retirementTargetNetWorth,
    c: params.annualRetirementContribution,
    ry: params.retirementYear,
    pre: params.postRetirementExpenses,
  }
  const compressed = deflate(JSON.stringify(payload))
  const b64 = toBase64url(compressed)
  // Use the current origin so the link works on both localhost and production.
  const origin = window.location.origin
  return `${origin}/#import/v2/${b64}`
}

/** Decode a share URL fragment.
 *  Supports:
 *    #import/v2/<deflate+base64url>  — compressed (current)
 *    #v1/<plain-base64url>           — uncompressed (legacy, backwards compat)
 */
export function decodeShareFragment(fragment: string): SharePayload | null {
  try {
    // v2 compressed: #import/v2/<b64>
    const v2 = fragment.match(/^#?import\/v2\/(.+)$/)
    if (v2) {
      const json = inflate(v2[1])
      const parsed = JSON.parse(json) as SharePayload
      if (parsed.v !== 1 || typeof parsed.p?.birthYear !== 'number') return null
      return parsed
    }
    // v1 plain: #v1/<b64> (legacy)
    const v1 = fragment.match(/^#?v1\/(.+)$/)
    if (v1) {
      const bytes = fromBase64urlBytes(v1[1])
      const json = new TextDecoder().decode(bytes)
      const parsed = JSON.parse(json) as SharePayload
      if (parsed.v !== 1 || typeof parsed.p?.birthYear !== 'number') return null
      return parsed
    }
    return null
  } catch {
    return null
  }
}

/** Apply a decoded share payload on top of base params.
 *  Input clamp: numeric profile fields are bounded to prevent crafted URLs
 *  from triggering excessive computation or NaN propagation in the engine.
 */
export function applySharePayload(base: SimulationParams, payload: SharePayload): SimulationParams {
  // Sanitize profile fields to engine-safe ranges
  const p = payload.p
  const safeProfile: UserProfile = {
    birthYear:                   clampInt(p.birthYear, 1930, 2010),
    partnerBirthYear:            p.partnerBirthYear > 0 ? clampInt(p.partnerBirthYear, 1930, 2010) : 0,
    childrenCount:               clampInt(p.childrenCount ?? 0, 0, 20),
    annualW2Income:              clampNum(p.annualW2Income, 0, 10_000_000),
    annualEquityComp:            clampNum(p.annualEquityComp ?? 0, 0, 10_000_000),
    equityCompYears:             clampInt(p.equityCompYears ?? 0, 0, 50),
    annualSelfEmploymentIncome:  clampNum(p.annualSelfEmploymentIncome ?? 0, 0, 10_000_000),
    liquidAssets:                clampNum(p.liquidAssets ?? 0, 0, 100_000_000),
    retirementAssets:            clampNum(p.retirementAssets ?? 0, 0, 100_000_000),
    homeEquity:                  clampNum(p.homeEquity ?? 0, 0, 50_000_000),
    annualHousingExpense:        clampNum(p.annualHousingExpense ?? 0, 0, 2_000_000),
    annualOtherExpenses:         clampNum(p.annualOtherExpenses ?? 0, 0, 2_000_000),
  }
  return {
    ...base,
    profile: safeProfile,
    events: payload.ev ?? [],
    allocation: payload.al,
    returns: { ...base.returns, equityMean: clampNum(payload.eq, -0.5, 1.0) },
    inflation: { ...base.inflation, mean: clampNum(payload.inf, 0, 0.5) },
    retirementTargetNetWorth: clampNum(payload.ret, 0, 500_000_000),
    annualRetirementContribution: clampNum(payload.c, 0, 100_000),
    retirementYear: clampInt(payload.ry, 2026, 2100),
    postRetirementExpenses: clampNum(payload.pre, 0, 2_000_000),
  }
}

function clampNum(v: unknown, min: number, max: number): number {
  const n = typeof v === 'number' && isFinite(v) ? v : 0
  return Math.max(min, Math.min(max, n))
}

function clampInt(v: unknown, min: number, max: number): number {
  return Math.round(clampNum(v, min, max))
}

/** Read share payload from the current URL fragment if present. */
export function readShareFromURL(): SharePayload | null {
  return decodeShareFragment(window.location.hash)
}

/** Remove the share fragment from the current URL (after applying it). */
export function clearShareFragment(): void {
  if (window.location.hash.startsWith('#import/') || window.location.hash.startsWith('#v1/')) {
    history.replaceState(null, '', window.location.pathname + window.location.search)
  }
}
